import {Component, Input, inject, OnInit, Output, EventEmitter, HostListener} from '@angular/core';
import {Subscription, firstValueFrom, from, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {CartRequestDetails} from '../../../../@domain/cart-request-details';
import * as fromApp from '../../../../../app/app.reducer';
import {ApiBaseService} from "../../../../../../projects/vendors/src/lib/services/api-base.service";
import {getPlaceOrderState} from "../../../../app.reducer";
import {ApiEnum} from "../../../../@application/enum/api.enum";
import {ADD_PRINTED_FOODS_TO_CART, CLEAR_CART, DELETE_FOOD_FORM_CART, UPDATE_CART_ITEM_NAMES} from "../../store/place-order/place-order.action";
import {CheckOutDialogBoxComponent} from "../../ongoing-orders/check-out-dialog-box/check-out-dialog-box.component";
import {MatDialog} from "@angular/material/dialog";
import {Router} from "@angular/router";
import {PathEnum} from "../../../../@application/enum/path.enum";
import {StorageService} from "../../../../../../projects/vendors/src/lib/services/storage.service";
import {OrderFoods} from "../../../../@domain/order-foods";
import {FoodItemQuantity} from "../../../../@domain/food-item-quantity";
import {CartDeleteDialogComponent} from "./cart-delete-dialog/cart-delete-dialog.component";
import {FoodItem} from '../../../../@domain/food-item';
import {BaseComponent} from "../../../../@application/component/base.component";
import {FoodItemPipe} from "../../../../../../projects/vendors/src/lib/pipe/food-item.pipe";
import {KotPrintDialogComponent} from "./kot-print-dialog/kot-print-dialog.component";
import {RestaurantTable} from "../../../../@domain/restaurent-table";

@Component({
    selector: 'lib-order-cart',
    templateUrl: './order-cart.component.html',
    styleUrl: './order-cart.component.css',
    standalone: false
})

export class OrderCartComponent extends BaseComponent implements OnInit {

  foodItemSubscription!: Subscription
  cartFoods: CartRequestDetails[] = [];
  apiBaseService = inject(ApiBaseService)
  dialog = inject(MatDialog)
  router = inject(Router)
  private foodNamePipe = inject(FoodItemPipe)
  @Input() orderId!: string
  @Input() orderDetails = {} as OrderFoods
  @Input() tables = [] as RestaurantTable[]
  @Output() foodSubmitted: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() orderPrinted: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() orderDetailsUpdated: EventEmitter<OrderFoods> = new EventEmitter<OrderFoods>();
  isOrderPrinted = false
  foodItemQuantity = [] as FoodItemQuantity[]
  selectedCartIndex = -1;
  lastEnterTime = 0;
  foodItems = [] as FoodItem[];
  private store = inject(Store<fromApp.AppState>)


  ngOnInit(): void {
    this.subscribe()
  }

  private subscribe() {
    this.subscription$.add(
      this.store.select(getPlaceOrderState)
        .subscribe({
          next: value => {
            this.cartFoods = value.cartFoods;

            this.isOrderPrinted = this.cartFoods.every(x => x.id !== undefined)
            if (this.isOrderPrinted && this.cartFoods.length === 0) {
              this.isOrderPrinted = false
            }
            this.orderPrinted.emit(this.isOrderPrinted)
          }
        })
    )
    this.subscription$.add(
      this.store.select(fromApp.getMenuState)
        .subscribe({
          next: value => {
            this.foodItems = value.menuRelatedData.foodItem
            this.foodItemQuantity = value.menuRelatedData.quantity;

            // Re-populate cart item names if foodItems just loaded and cart has items without names
            if (this.foodItems.length > 0 && this.cartFoods.length > 0) {
              this.populateCartItemNames();
            }
          }
        })
    )
  }

  private populateCartItemNames(): void {
    // Check if any cart items are missing names
    const hasItemsWithoutNames = this.cartFoods.some(item => !item.name);
    if (!hasItemsWithoutNames) return;

    const foodNameLookup = new Map(this.foodItems.map(item => [item.id, item.name]));

    // Dispatch action to update cart foods with names through the store
    this.store.dispatch(UPDATE_CART_ITEM_NAMES({payload: foodNameLookup}));
  }

  onDeleteFood(foodItem: CartRequestDetails): void {
    if (foodItem.id !== undefined) {
      const dialogRef = this.dialog.open(CartDeleteDialogComponent, {
        width: '25%',
        data: foodItem.name
      })
      dialogRef.afterClosed().subscribe({
        next: value => {
          // The dialog closes with the reason on confirm, and with true on cancel.
          if (value != true) {
            this.deleteFood(value, foodItem)
          }
        }
      })
    } else {
      this.store.dispatch(DELETE_FOOD_FORM_CART({payload: foodItem}))
    }
  }

  onDeleteClick(event: Event, foodItem: CartRequestDetails): void {
    event.stopPropagation();
    this.onDeleteFood(foodItem);
  }

  isNameContainsSpecialCharacter(name: string): boolean {
    const foodName = this.foodNamePipe.transform(name, this.foodItems);
    const bracketsRegex = /[()]/;
    return bracketsRegex.test(foodName);
  }

  private deleteFood(deletedReason: string, foodItem: CartRequestDetails): void {
    this.apiBaseService.POST_API([ApiEnum.RestaurantModule, ApiEnum.Cart, ApiEnum.Delete], {
      id: foodItem.id,
      deleteReason: deletedReason,
      isAdminApproved: true
    }, true, true, 'Successfully Deleted!').subscribe({
      next: async () => {
        this.store.dispatch(DELETE_FOOD_FORM_CART({payload: foodItem}))
        await this.refreshCartFromServer()
      }
    })
  }

  getSubtotal(): number {
    let netTotal = 0;
    let printedFoods = this.cartFoods.filter(x => x.id !== undefined);
    let notPrintedFoods = this.cartFoods.filter(x => x.id === undefined);
    if (printedFoods.length > 0) {
      printedFoods.forEach(food => {
        netTotal += food.price;
      });
    }
    if (notPrintedFoods.length > 0) {
      notPrintedFoods.forEach(food => {
        netTotal += food.price * food.quantity;
      });
    }
    return netTotal;
    // return this.cartFoods.reduce((acc, item) => acc + item.price * item.quantity, 0);
  }

  onCreateOrder(status: string) {
    this.createOrderRequest(status)
      .subscribe({
        next: _res => {
          if (status === 'stay') {
            return;
          }
          this.foodSubmitted.emit(true);
        }
      })

  }

  private createOrderRequest(status: string) {
    const data = {
      orderId: this.orderId,
      cartRequestDetails: this.cartFoods.filter(x => x.id === undefined)
    }
    return this.apiBaseService.POST_API([ApiEnum.RestaurantModule, ApiEnum.Cart], data)
      .pipe(
        switchMap(res => {
          if (status !== 'stay') {
            return of(res);
          }
          return from(this.refreshCartFromServer()).pipe(map(() => res));
        })
      );
  }

  private async refreshCartFromServer(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiBaseService.GET<OrderFoods[]>([ApiEnum.RestaurantModule, ApiEnum.Cart, ApiEnum.Order, this.orderId], false)
      );

      if (!response?.length) {
        this.store.dispatch(ADD_PRINTED_FOODS_TO_CART({payload: []}));
        return;
      }

      this.orderDetails = response[0];
      this.orderDetailsUpdated.emit(this.orderDetails);
      const foodNameLookup = new Map(this.foodItems.map(item => [item.id, item.name]));
      const updatedFoods = this.orderDetails.cartResponseDetails.map(food => ({
        id: food.id,
        foodId: food.foodId,
        name: foodNameLookup.get(food.foodId) ?? this.foodNamePipe.transform(food.foodId, this.foodItems),
        menuId: food.menuId,
        kitchenId: food.kitchenId,
        price: food.totalPrice,
        quantity: food.quantity,
        kitchenIntroduction: food.kitchenIntroduction ?? '',
        extras: food.extras?.length ? food.extras : null,
        foodQuantityTagId: food.foodQuantityTagId,
        totalCost: 0,
        cartStatus: food.cartStatus,
        isServiceCharge: food.isServiceCharge
      }));

      this.store.dispatch(ADD_PRINTED_FOODS_TO_CART({payload: updatedFoods}));
    } catch (error) {
      console.error('Failed to refresh cart after saving order', error);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (!this.isOrderPrinted) {
      $event.returnValue = true; // Display confirmation prompt
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Skip if a dialog is open
    const dialogOpen = document.querySelector('.cdk-overlay-pane mat-dialog-container');
    if (dialogOpen) {
      return;
    }

    // Skip if user is typing in an input field
    const activeElement = document.activeElement;
    const isInputFocused = activeElement instanceof HTMLInputElement ||
                           activeElement instanceof HTMLTextAreaElement;
    if (isInputFocused) return;

    // Ctrl+S - Create order (stay)
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault();
      if (!this.isOrderPrinted) {
        this.onCreateOrder('stay');
      }
      return;
    }

    // Ctrl+P - Print KOT
    if (event.ctrlKey && event.key === 'p') {
      event.preventDefault();
      this.onPrintKot();
      return;
    }

    // Double Enter - Checkout
    if (event.key === 'Enter') {
      const now = Date.now();
      if (now - this.lastEnterTime < 300) {
        event.preventDefault();
        this.onCloseOrder();
        this.lastEnterTime = 0;
        return;
      }
      this.lastEnterTime = now;
      return;
    }

    // Arrow Up - Navigate cart items up
    if (event.key === 'ArrowUp' && event.shiftKey) {
      event.preventDefault();
      this.navigateCart('up');
      return;
    }

    // Arrow Down - Navigate cart items down
    if (event.key === 'ArrowDown' && event.shiftKey) {
      event.preventDefault();
      this.navigateCart('down');
      return;
    }

    // Delete key - Delete selected cart item
    if (event.key === 'Delete' && this.selectedCartIndex >= 0) {
      event.preventDefault();
      const selectedItem = this.cartFoods[this.selectedCartIndex];
      if (selectedItem) {
        this.onDeleteFood(selectedItem);
        // Clear selection after initiating delete
        this.selectedCartIndex = -1;
      }
      return;
    }

    // Escape - Clear cart selection
    if (event.key === 'Escape') {
      this.selectedCartIndex = -1;
      return;
    }
  }

  private navigateCart(direction: 'up' | 'down'): void {
    if (!this.cartFoods || this.cartFoods.length === 0) return;

    if (direction === 'up') {
      this.selectedCartIndex = this.selectedCartIndex > 0
        ? this.selectedCartIndex - 1
        : this.cartFoods.length - 1;
    } else {
      this.selectedCartIndex = this.selectedCartIndex < this.cartFoods.length - 1
        ? this.selectedCartIndex + 1
        : 0;
    }

    // Scroll selected item into view
    setTimeout(() => {
      const cartElements = document.querySelectorAll('.swipe-container');
      const element = cartElements[this.selectedCartIndex] as HTMLElement;
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }

  getTotalQuantity(): number {
    return this.cartFoods?.reduce((sum, item) => sum + item.quantity, 0);
  }

  async onCloseOrder() {
    const notSavedFoods = this.cartFoods.filter(x => x.id === undefined)
    if (notSavedFoods.length > 0) {
      try {
        await firstValueFrom(this.createOrderRequest('stay'))
      } catch (error) {
        return
      }
    }
    const dialogRef = this.dialog.open(CheckOutDialogBoxComponent, {
      width: '70vw',
      maxWidth: '70vw',
      height: '90vh',
      maxHeight: '90vh',
      panelClass: 'checkout-dialog-panel',
      data: {
        onGoingOrder: this.orderDetails,
        foods: this.foodItems,
        tables: this.tables
      }
    })
    dialogRef.afterClosed().subscribe({
      next: value => {
        // Only a settled bill closes with a value; ESC/backdrop must stay on the order.
        if (value) {
          // Clear after navigation resolves - canDeactivate() blocks on an empty cart.
          this.router.navigate([PathEnum.Home, PathEnum.TableOccupancy])
            .then(() => this.store.dispatch(CLEAR_CART()))
        }
      }
    })
  }

  onPrintKot() {
    this.dialog.open(KotPrintDialogComponent, {
      width: '70vw',
      maxWidth: '70vw',
      height: '90vh',
      maxHeight: '90vh',
      panelClass: 'kot-print-panel',
      data: {
        orderId: this.orderId,
        cartFoods: this.cartFoods,
        orderDetails: this.orderDetails,
        foodItems: this.foodItems
      }
    })
  }
}
